import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency } from "@/lib/utils";
import type { DashboardViewProps } from "@/ui/view-props";
import { Camera, Flame, History, Plus, TrendingUp, Handshake, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { CountUp } from "@/components/ui/count-up";

/* ── Loading skeleton ─────────────────────────────────────── */
function DashboardSkeleton() {
  return (
    <div className="space-y-6 py-2 animate-fade-in">
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-32 rounded-md" />
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

/* ── Rank badge ───────────────────────────────────────────── */
function RankBadge({ rank, index }: { rank: string; index: number }) {
  const medals = ["🥇", "🥈", "🥉"];
  if (index < 3) return <span className="text-base leading-none">{medals[index]}</span>;
  return (
    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">
      {index + 1}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────── */
export function DashboardModernView(p: DashboardViewProps) {
  const navigate = useNavigate();
  const targetPrefix = p.isReadOnly ? `/public/${p.targetUserId}` : "";

  if (p.loading) return <DashboardSkeleton />;

  const handlePlayerClick = (playerId: string) => {
    navigate(p.isReadOnly ? `/public/${p.targetUserId}/player/${playerId}` : `/player/${playerId}`);
  };

  return (
    <div className="space-y-8 pb-6 animate-fade-in">

      {/* ── Active session banner ─────────────────────────── */}
      {p.activeSessionId && !p.isReadOnly && (
        <Card className="border-primary/30 bg-primary/5 shadow-card-md">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <Flame className="h-5 w-5 text-primary animate-pulse" />
              </div>
              <div>
                <p className="text-sm font-bold">Active Session</p>
                <p className="text-xs text-muted-foreground">Session in progress</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-muted-foreground"
                onClick={() => {
                  const url = `${window.location.origin}/live/${p.activeSessionId}`;
                  navigator.clipboard.writeText(url).then(() =>
                    alert("Live link copied! Share it with spectators."),
                  );
                }}
              >
                Copy Live Link
              </Button>
              <Button size="sm" variant="destructive" onClick={p.onAbortSession}>
                Abort
              </Button>
              <Link to="/session/new">
                <Button size="sm" className="font-semibold">Resume →</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Quick actions ─────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {!p.isReadOnly && (
          <>
            <Link to="/session/new" className="col-span-1">
              <button className={cn(
                "w-full h-[88px] rounded-xl flex flex-col items-center justify-center gap-2",
                "bg-primary text-primary-foreground font-semibold text-sm",
                "shadow-card hover:shadow-card-md transition-all duration-150 active:scale-[0.97]",
                "select-none",
              )}>
                <Plus className="h-5 w-5" />
                New Session
              </button>
            </Link>
            <Link to="/session/import" className="col-span-1">
              <button className={cn(
                "w-full h-[88px] rounded-xl flex flex-col items-center justify-center gap-2",
                "bg-secondary text-secondary-foreground font-semibold text-sm",
                "border border-border/60 shadow-xs hover:shadow-card transition-all duration-150 active:scale-[0.97]",
                "select-none",
              )}>
                <Camera className="h-5 w-5" />
                Import
              </button>
            </Link>
          </>
        )}
        <Link
          to={p.isReadOnly ? `/public/${p.targetUserId}/sessions` : "/sessions"}
          className={cn(!p.isReadOnly ? "col-span-1" : "col-span-2")}
        >
          <button className={cn(
            "w-full h-[88px] rounded-xl flex flex-col items-center justify-center gap-2",
            "bg-card text-foreground font-semibold text-sm",
            "border border-border/60 shadow-xs hover:shadow-card transition-all duration-150 active:scale-[0.97]",
            "select-none",
          )}>
            <History className="h-5 w-5 text-muted-foreground" />
            Sessions
          </button>
        </Link>
        {!p.isReadOnly && (
          <Link to="/settlement" className="col-span-1">
            <button className={cn(
              "w-full h-[88px] rounded-xl flex flex-col items-center justify-center gap-2",
              "bg-card text-foreground font-semibold text-sm",
              "border border-border/60 shadow-xs hover:shadow-card transition-all duration-150 active:scale-[0.97]",
              "select-none",
            )}>
              <Handshake className="h-5 w-5 text-muted-foreground" />
              Settle Up
            </button>
          </Link>
        )}
      </div>

      {/* ── Main content grid ─────────────────────────────── */}
      <div className="grid gap-6 md:grid-cols-12">
        <div className="md:col-span-8 space-y-6">

          {/* Recent settled session */}
          {p.settings.recent_results && p.recentSettledSession && (
            <section className="space-y-3">
              <div className="section-label">Recent Activity</div>
              <Link
                to={p.isReadOnly
                  ? `/public/${p.targetUserId}/session/${p.recentSettledSession.id}`
                  : `/session/${p.recentSettledSession.id}`
                }
              >
                <Card className="hover:shadow-card-md transition-shadow duration-150 group">
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">Last settled session</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {format(new Date(p.recentSettledSession.date), "MMMM d, yyyy")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="success">Settled</Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </section>
          )}

          {/* Unsettled standings */}
          {p.unsettledStats.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="section-label">Unsettled Standings</div>
                <Link
                  to={p.isReadOnly ? `/public/${p.targetUserId}/who-pays-who` : "/who-pays-who"}
                  className="text-xs text-primary font-semibold hover:underline underline-offset-4 flex items-center gap-0.5"
                >
                  Who Pays Who <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-border/60">
                    {p.unsettledStats.map((player, i) => (
                      <button
                        key={player.id}
                        className="w-full p-4 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors text-left first:rounded-t-xl last:rounded-b-xl"
                        onClick={() => handlePlayerClick(player.id)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <RankBadge rank="" index={i} />
                          <PlayerAvatar name={player.name} avatarUrl={player.avatar_url} size={32} />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{player.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {player.totalSessions} sessions · {formatCurrency(player.totalSpent)} spent
                            </div>
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm font-mono font-bold tabular-nums shrink-0",
                          player.totalNet > 0
                            ? "text-success"
                            : player.totalNet < 0
                            ? "text-destructive"
                            : "text-muted-foreground",
                        )}>
                          {player.totalNet >= 0 ? "+" : ""}
                          <CountUp end={player.totalNet} formatter={formatCurrency} />
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {/* Unsettled expenses breakdown */}
          {p.unsettledExpenses.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="section-label">Unsettled Expenses</div>
                <Link
                  to={p.isReadOnly ? `/public/${p.targetUserId}/expenses` : "/expenses"}
                  className="text-xs text-primary font-semibold hover:underline underline-offset-4 flex items-center gap-0.5"
                >
                  All Expenses <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-border/60">
                    {p.unsettledExpenses.map((exp) => (
                      <div key={exp.id} className="p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{exp.description || 'Expense'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            Paid by {exp.payer?.name} · {exp.participants?.length} participants
                          </div>
                        </div>
                        <div className="text-sm font-mono font-bold text-destructive tabular-nums shrink-0">
                          {formatCurrency(exp.amount)}
                        </div>
                      </div>
                    ))}
                    <div className="p-4 flex items-center justify-between gap-3 bg-muted/20 rounded-b-xl">
                      <div className="text-sm font-bold">Total Unsettled</div>
                      <div className="text-sm font-mono font-bold tabular-nums">
                        {formatCurrency(p.unsettledExpenses.reduce((sum, e) => sum + Number(e.amount), 0))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {/* All-time standings */}
          {p.allTimeStats.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="section-label">Lifetime Standings</div>
                <Link to={p.isReadOnly ? `/public/${p.targetUserId}/statistics` : "/statistics"}>
                  <Button size="sm" variant="ghost" className="text-xs gap-1 h-7">
                    <TrendingUp className="h-3 w-3" />
                    Full Stats
                  </Button>
                </Link>
              </div>
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-border/60">
                    {p.allTimeStats.slice(0, 5).map((player, i) => (
                      <button
                        key={player.id}
                        className="w-full p-4 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors text-left first:rounded-t-xl last:rounded-b-xl"
                        onClick={() => handlePlayerClick(player.id)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <RankBadge rank={player.rank} index={i} />
                          <PlayerAvatar name={player.name} avatarUrl={player.avatar_url} size={32} />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{player.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {player.totalSessions} sessions · {formatCurrency(player.totalSpent)} spent
                            </div>
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm font-mono font-bold tabular-nums shrink-0",
                          player.totalNet > 0
                            ? "text-success"
                            : player.totalNet < 0
                            ? "text-destructive"
                            : "text-muted-foreground",
                        )}>
                          {player.totalNet >= 0 ? "+" : ""}
                          <CountUp end={player.totalNet} formatter={formatCurrency} />
                        </div>
                      </button>
                    ))}
                    {p.allTimeStats.length > 5 && (
                      <Link to={p.isReadOnly ? `/public/${p.targetUserId}/statistics` : "/statistics"}>
                        <button className="w-full h-12 text-xs text-muted-foreground hover:bg-muted/40 transition-colors rounded-b-xl flex items-center justify-center gap-1">
                          View all {p.allTimeStats.length} players
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
        </div>

        {/* ── Side panel ──────────────────────────────────── */}
        <div className="md:col-span-4 space-y-6">
          {p.settings.whos_hot && p.hotPlayers.length > 0 && (
            <section className="space-y-3">
              <div className="section-label">Who's Hot?</div>
              <Card>
                <CardContent className="p-4 space-y-3">
                  {p.hotPlayers.slice(0, 5).map((h) => (
                    <div key={h.name} className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium truncate">{h.name}</div>
                      <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-0.5 rounded-full shrink-0 border border-amber-200/50 dark:border-amber-800/30">
                        <Flame className="h-3 w-3" />
                        {h.streak}W
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
